import {
    Button,
    Col,
    Divider,
    Drawer,
    Form,
    Input,
    Modal,
    Row,
    Select,
    Upload,
    UploadFile,
    notification,
    InputNumber
} from 'antd';
import TextArea from 'antd/es/input/TextArea';
import { Option } from 'antd/es/mentions';
import { RcFile, UploadProps } from 'antd/es/upload';
import React, { useEffect, useState} from 'react';
import {Icon} from 'ts-react-feather-icons';
import {ActionType} from '../../constants/actionType';
import {createStory, updateStory} from '../../helpers/api/story';
import './StoryDrawer.scss';
import {convertToWebP} from "../../utils/image";

const storyDrawerValidation = {
    name: [
        {required: true, message: 'Name is required'},
        {max: 100, message: 'Name must be less than 100 characters'},
    ],
    code: [
        {max: 50, message: 'Code must be less than 50 characters'},
    ],
    description: [
        {max: 5000, message: 'Development direction must be less than 5000 characters'},
    ],
    status: [
        {required: true, message: 'Status is required'},
    ],
    level: [
        {required: true, message: 'Level is required'},
    ],
}

const StoryDrawer = ({
                         drawerVisibility,
                         setdDrawerVisibility,
                         storyDetail,
                         setStoryDetail,
                         actionType,
                         setActionType,
                         onSuccess,
                         onFailure
                     }: any) => {

    const [form] = Form.useForm();
    const formValueObj = Form.useWatch([], form);

    const [submittables, setSubmittable] = useState<boolean>(false);
    const [loadingBtn, setLoadingBtn] = useState<boolean>(false);

    const [storyStatus, setStoryStatus] = useState<string>("inactive");

    const [api, contextHolder] = notification.useNotification();

    // for image
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewImage, setPreviewImage] = useState('');
    const [images, setImages] = useState<UploadFile[]>([]);

    // images
    const getBase64 = (file: RcFile): Promise<string> =>
        new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = (error) => reject(error);
        });

    const handleCancel = () => setPreviewOpen(false);

    const handlePreview = async (file: UploadFile) => {
        if (!file.url && !file.preview) {
            file.preview = await getBase64(file.originFileObj as RcFile);
        }

        setPreviewImage(file.url || (file.preview as string));
        setPreviewOpen(true);
    };

    const handleChange: UploadProps['onChange'] = async ({ fileList: newFileList }) => {
        try {
            // Tạo một danh sách fileList tạm thời để hiển thị ngay lập tức
            const tempFileList = newFileList.map(file => ({
                ...file,
                status: 'uploading' as UploadFile<any>['status'],
            }));
            setImages(tempFileList as UploadFile<any>[]);

            // Convert files sang WebP
            const webpFiles = await Promise.all(
                newFileList.map(async (file) => {
                    // Chỉ convert nếu là file mới upload
                    if (!file.originFileObj) {
                        return file;
                    }

                    try {
                        const webpFile = await convertToWebP(file.originFileObj as RcFile);
                        // Tạo preview URL cho file đã convert
                        const previewUrl = await getBase64(webpFile);

                        return {
                            ...file,
                            originFileObj: webpFile,
                            status: 'done',
                            preview: previewUrl,
                            url: previewUrl,
                            fileName: webpFile.name
                        };
                    } catch (error) {
                        return {
                            ...file,
                            status: 'error',
                            error: new Error('Failed to convert image')
                        };
                    }
                })
            );

            setImages(webpFiles as UploadFile<any>[]);
        } catch (error) {
            console.error('Error processing files:', error);
            notification.error({
                message: 'Error',
                description: 'Failed to process image'
            });
        }
    };


    const uploadButton = (
        <div>
            {/* <PlusOutlined rev={1} /> */}
            <Icon name="plus" size={16}/>
            <div style={{marginTop: 8}}>Upload</div>
        </div>
    );

    useEffect(() => {
        form.validateFields({validateOnly: true})
            .then(
                () => {
                    setSubmittable(true)
                },
                () => {
                    setSubmittable(false)
                },
            );
    }, [formValueObj]);


    useEffect(() => {
        if (actionType === ActionType.CREATE) {
            form.resetFields();
            setSubmittable(false);
            setImages([]);
            form.setFieldValue("status", "inactive");
        }
    }, [actionType]);

    useEffect(() => {
        if (!storyDetail) {
            form.setFieldValue("status", "inactive");
            setImages([]);
            return;
        }
        if (storyDetail?.defaultImageUrl && storyDetail?.defaultImageUrl?.trim().length > 0) {
            const urlTemp = storyDetail?.defaultImageUrl.split("/");
            setImages([{
                uid: "1",
                name: urlTemp[urlTemp.length - 1],
                status: 'done',
                url: storyDetail?.defaultImageUrl
            }]);
        } else {
            setImages([]);
        }
        form.setFieldsValue({...storyDetail});
        setStoryStatus(storyDetail.status);
    }, [storyDetail]);

    const onClose = () => {
        form.resetFields();
        setStoryDetail(null);
        setImages([]);
        form.setFieldValue("status", "inactive");
        setSubmittable(false);
        setdDrawerVisibility(false);
        setActionType("");
    }

    const saveStory = async () => {
        try {
            form.validateFields();

            setLoadingBtn(true);
            let response;
            const requestModel: any = {
                name: formValueObj.name?.trim(),
                code: formValueObj.code?.trim(),
                status: storyStatus,
                description: formValueObj.description?.trim(),
                level: formValueObj.level,
            }
            if (actionType === ActionType.CREATE) {
                response = await createStory({
                    data: JSON.stringify(requestModel),
                    image: images.length > 0 ? images?.[0]?.originFileObj : null
                });
            } else {
                const requestBody: any = {};
                if (images?.[0] && images?.[0].url === storyDetail.defaultImageUrl) {
                    requestModel["defaultImageUrl"] = storyDetail.defaultImageUrl;
                } else {
                    requestBody["image"] = images?.[0]?.originFileObj;
                }
                requestBody["data"] = JSON.stringify(requestModel);
                response = await updateStory(storyDetail.id, requestBody);
            }

            setLoadingBtn(false);
            if (response?.status === 200) {
                onSuccess();
                onClose();
                return;
            }
        } catch (err) {
            console.log(err);
            onFailure(err);
        }
        setLoadingBtn(false);
    }

    return (
        <Drawer
            title={(actionType === ActionType.UPDATE ? "Update" : "Create") + " a story"}
            className='story-drawer'
            width={750}
            onClose={() => {
                setdDrawerVisibility(false)
            }}
            open={drawerVisibility}
            closeIcon={false}
            footer={
                <div className='d-flex justify-content-center'>
                    <Button onClick={saveStory} type="primary" className='me-3' disabled={!submittables}
                            loading={loadingBtn}>
                        Save
                    </Button>
                    <Button onClick={onClose}>Cancel</Button>
                </div>
            }
        >
            {contextHolder}
            <Form layout="vertical" autoComplete='off' form={form}>
                <Divider orientation="left" className='text-danger'>01. STORY IMAGE</Divider>
                <Row gutter={16} className='d-flex justify-content-center'>
                    <Form.Item
                        name="image"
                        label="Image"
                    >
                        <Upload
                            listType="picture-card"
                            fileList={images}
                            onPreview={handlePreview}
                            onChange={handleChange}
                            multiple={false}
                            accept="image/*"
                        >
                            {images.length >= 1 ? null : uploadButton}
                        </Upload>
                        <Modal open={previewOpen} footer={null} onCancel={handleCancel}>
                            <img alt="example" style={{width: '100%'}} src={previewImage}/>
                        </Modal>
                    </Form.Item>
                </Row>
                <Divider orientation="left" className='text-danger'>02. STORY COMMON INFORMATION</Divider>
                <Row gutter={16} className='mb-3'>
                    <Col span={12}>
                        <Form.Item
                            name="name"
                            label="Name"
                            rules={storyDrawerValidation.name}
                        >
                            <Input placeholder="Please enter name"/>
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="level"
                            label="Level"
                            rules={storyDrawerValidation.level}
                        >
                            <InputNumber placeholder="Please enter level" min={1} max={100} style={{ width: "100%" }} />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={16} className='mb-3'>
                    <Col span={12}>
                        <Form.Item
                            name="status"
                            label="Status"
                            rules={storyDrawerValidation.status}
                        >
                            <Select
                                placeholder="Please select a status"
                                value={storyStatus}
                                onChange={(e) => setStoryStatus(e)}
                                disabled={actionType === ActionType.CREATE}
                            >
                                <Option value="active">Active</Option>
                                <Option value="inactive">Inactive</Option>
                            </Select>
                        </Form.Item>
                    </Col>
                </Row>


                <Row gutter={16}>
                    <Col span={24}>
                        <Form.Item
                            name="description"
                            label="Description"
                            rules={storyDrawerValidation.description}
                        >
                            <TextArea rows={4} placeholder="Enter description" maxLength={5000} />
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
        </Drawer>
    )

}

export default StoryDrawer;
